import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Toolbar,
  AppBar,
  Typography,
  CssBaseline,
  Divider,
  Tooltip,
  useMediaQuery,
  Avatar,
  Collapse,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Dashboard as DashboardIcon,
  Work as WorkIcon,
  ExitToApp as ExitToAppIcon,
  BusinessCenter,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../app-store/authSlice";
import {
  setUser,
  setUserLoading,
  setUserError,
  clearUser,
} from "../app-store/userSlice";

const drawerWidth = 260;
const miniDrawerWidth = 72;
const TRANSITION = "width 0.25s ease, margin 0.25s ease";

const knownRoutes = [
  "/",
  "/auth/login",
  "/auth/signup",
  "/auth/forgot-password",
  "/auth/email-sent",
  "/auth/reset-password",
  "/auth/success",
  "/user/dashboard",
  "/jobs",
];

const SidebarLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const token = useSelector((state) => state.auth.token);
  const userId = useSelector((state) => state.auth.userId);
  const profile = useSelector((state) => state.user.data);
  const userLoading = useSelector((state) => state.user.loading);
  const isMobile = useMediaQuery("(max-width:600px)");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMounted = useRef(false);

  const isAuthPage = [
    "/auth/login",
    "/auth/signup",
    "/auth/forgot-password",
    "/auth/email-sent",
    "/auth/reset-password",
    "/auth/success",
  ].includes(location.pathname);

  const isHomePage = location.pathname === "/";
  const isNotFound = !knownRoutes.includes(location.pathname);
  const hideSidebar = isAuthPage || isHomePage || !token;

  const toggleDrawer = () => setOpen((prev) => !prev);

  const handleUserClick = (event) => {
    if (open) {
      setUserMenuOpen((prev) => !prev);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => setAnchorEl(null);

  useEffect(() => {
    if (!open) setUserMenuOpen(false);
  }, [open]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (isMobile) setOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (!token || !userId) return;
    if (profile || userLoading) return;

    const fetchProfile = async () => {
      dispatch(setUserLoading());
      try {
        const { data } = await axios.get(
          `https://le-travaille-server.onrender.com/user/${userId}`,
          { headers: { token } },
        );
        dispatch(setUser(data));
      } catch (err) {
        console.error("Sidebar profile fetch error:", err);
        dispatch(
          setUserError({
            message: err.response?.data?.message || "Failed to load profile",
            status: err.response?.status || null,
          }),
        );
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearUser());
    navigate("/");
  };

  const menuItems = token
    ? [
        { text: "Dashboard", icon: <DashboardIcon />, path: "/user/dashboard" },
        { text: "Jobs", icon: <WorkIcon />, path: "/jobs" },
      ]
    : [{ text: "Job Seeker", icon: <BusinessCenter />, path: "/auth/login" }];

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const currentDrawerWidth = open ? drawerWidth : miniDrawerWidth;

  const Logo = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          overflow: "hidden",
          bgcolor: "#e8edf2",
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="https://i.ibb.co/mh7GRRf/Man-Searching-for-a-Job-removebg.png"
          alt="Le-Travaille logo"
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: "#FFD700",
          fontSize: "1.25rem",
          letterSpacing: "0.5px",
        }}
      >
        Le-Travaille
      </Typography>
    </Box>
  );

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#003d38",
      }}
    >
      {/* Sidebar Header */}
      <Toolbar
        sx={{
          minHeight: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "space-between" : "center",
          px: 2,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {open ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  overflow: "hidden",
                  bgcolor: "#e8edf2",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src="https://i.ibb.co/mh7GRRf/Man-Searching-for-a-Job-removebg.png"
                  alt="Le-Travaille logo"
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#FFD700",
                  fontSize: "15px",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                }}
              >
                Le-Travaille
              </Typography>
            </Box>
            <IconButton
              onClick={toggleDrawer}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "8px",
                p: "4px",
                "&:hover": {
                  bgcolor: "#ffffff",
                  color: "#00786F",
                  borderColor: "#ffffff",
                },
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          </>
        ) : (
          <IconButton
            onClick={toggleDrawer}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "8px",
              p: "4px",
              "&:hover": {
                bgcolor: "#ffffff",
                color: "#00786F",
                borderColor: "#ffffff",
              },
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        )}
      </Toolbar>

      <List sx={{ px: 1.5, py: 2, flex: 1 }}>
        {menuItems.map((item) => (
          <Tooltip
            key={item.text}
            title={!open ? item.text : ""}
            placement="right"
          >
            <ListItemButton
              component={NavLink}
              to={item.path}
              onClick={isMobile ? toggleDrawer : undefined}
              sx={{
                borderRadius: "10px",
                mb: 0.5,
                minHeight: 46,
                px: 1.5,
                justifyContent: open ? "initial" : "center",
                color: "#cbd5e1",
                "&:hover": {
                  bgcolor: "#ffffff",
                  color: "#00786F",
                  "& .MuiListItemIcon-root": { color: "#00786F" },
                },
                "&.active": {
                  bgcolor: "#ffffff",
                  color: "#00786F",
                  "& .MuiListItemIcon-root": { color: "#00786F" },
                },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, mr: open ? 1.5 : 0, color: "inherit" }}
              >
                {item.icon}
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>

      {/* User Footer */}
      {token && (
        <Box sx={{ px: 1.5, pb: 2 }}>
          <Divider sx={{ mb: 1.5, borderColor: "rgba(255,255,255,0.08)" }} />

          <ListItemButton
            onClick={handleUserClick}
            sx={{
              borderRadius: "10px",
              minHeight: 56,
              px: 1.5,
              justifyContent: open ? "initial" : "center",
              bgcolor: userMenuOpen ? "rgba(255,255,255,0.1)" : "transparent",
              "&:hover": {
                bgcolor: "#ffffff",
                color: "#00786F",
                "& .MuiListItemIcon-root": { color: "#00786F" },
                "& .MuiTypography-root": { color: "#00786F" },
                "& .MuiSvgIcon-root": { color: "#00786F" },
              },
            }}
          >
            <Avatar
              src={profile?.image_url}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#00786F",
                fontSize: "13px",
                fontWeight: 600,
                flexShrink: 0,
                mr: open ? 1.5 : 0,
              }}
            >
              {!profile?.image_url && getInitials(profile?.username)}
            </Avatar>

            {open && (
              <>
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {profile?.username || "User"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "rgba(255,255,255,0.45)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {profile?.email || ""}
                  </Typography>
                </Box>
                {userMenuOpen ? (
                  <ExpandLess
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: 18,
                      transition: "color 0.2s ease",
                    }}
                  />
                ) : (
                  <ExpandMore
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: 18,
                      transition: "color 0.2s ease",
                    }}
                  />
                )}
              </>
            )}
          </ListItemButton>

          {/* Expanded dropdown */}
          <Collapse in={userMenuOpen && open} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 0.5 }}>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: "10px",
                  minHeight: 44,
                  px: 1.5,
                  color: "#f87171",
                  "&:hover": {
                    bgcolor: "#ffffff",
                    color: "#dc2626",
                    "& .MuiListItemIcon-root": { color: "#dc2626" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: "inherit" }}>
                  <ExitToAppIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }}
                />
              </ListItemButton>
            </Box>
          </Collapse>

          {/* Collapsed popup menu — inside drawer so it positions relative to the avatar */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            disablePortal
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: "0 -4px 32px rgba(0,0,0,0.35)",
                minWidth: 210,
                overflow: "hidden",
                bgcolor: "#003d38",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.08)",
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  src={profile?.image_url}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "#00786F",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {!profile?.image_url && getInitials(profile?.username)}
                </Avatar>
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "14px",
                      lineHeight: 1.3,
                    }}
                  >
                    {profile?.username || "User"}
                  </Typography>
                  <Typography
                    sx={{ color: "rgba(255,255,255,0.45)", fontSize: "12px" }}
                  >
                    {profile?.email || ""}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                handleLogout();
              }}
              sx={{
                color: "#f87171",
                py: 1.5,
                px: 2,
                gap: 1.5,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.06)",
                  color: "#dc2626",
                },
              }}
            >
              <ExitToAppIcon fontSize="small" />
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );

  // 404 — no wrapper at all.
  if (isNotFound) return <>{children}</>;

  // Auth pages / Home / No token — AppBar only, no sidebar.
  if (hideSidebar) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: "#003d38",
            color: "#fff",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            width: "100%",
            zIndex: 1300,
          }}
        >
          <Toolbar sx={{ minHeight: 72 }}>
            <Logo />
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: isHomePage ? 0 : 3,
            width: "100%",
            backgroundColor: "#f5f7fa",
            minHeight: "100vh",
          }}
        >
          <Toolbar />
          {children}
        </Box>
      </Box>
    );
  }

  // Authenticated pages.
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: "#003d38",
            color: "#fff",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar
            sx={{
              minHeight: 72,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Logo />
            <IconButton
              onClick={toggleDrawer}
              aria-label="open sidebar"
              sx={{ color: "#fff" }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={isMobile ? toggleDrawer : undefined}
        sx={{
          "& .MuiDrawer-paper": {
            width: isMobile ? drawerWidth : currentDrawerWidth,
            overflowX: "hidden",
            transition: TRANSITION,
            border: "none",
            boxShadow: "1px 0 0 rgba(255,255,255,0.08)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          transition: TRANSITION,
          ml: isMobile ? 0 : `${currentDrawerWidth}px`,
          width: isMobile ? "100%" : `calc(100% - ${currentDrawerWidth}px)`,
          backgroundColor: "#f5f7fa",
        }}
      >
        {isMobile && <Toolbar sx={{ minHeight: "72px !important" }} />}
        {children}
      </Box>
    </Box>
  );
};

export default SidebarLayout;
